import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  // Tooltip,
  Dialog,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import DescriptionIcon from "@material-ui/icons/Description";
// import DoneAllIcon from "@material-ui/icons/DoneAll";
import { toast } from "react-toastify";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import Carrinho from "../components/Carrinho";
import api from "../services/api";
import { FaMicrophone, FaStop } from "react-icons/fa";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const AudioBubble = ({ src, isMobile = false }) => {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [duration, setDuration] = React.useState(0);

  // ✅ Mostra tempo formatado mm:ss
  const fmt = (sec) => {
    const s = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  // ✅ Eventos de tempo e metadata
  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTime = () => {
      if (el.duration > 0) setProgress(el.currentTime / el.duration);
    };
    const onLoaded = () => {
      setDuration(el.duration || 0);
    };
    const onEnded = () => setPlaying(false);

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("ended", onEnded);

    // 🔹 Se o metadata já estiver disponível (cacheado)
    if (el.readyState >= 1) {
      setDuration(el.duration || 0);
    }

    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("ended", onEnded);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => { });
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const seekClick = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    el.currentTime = ratio * el.duration;
    setProgress(ratio);
  };

  return (
    <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: isMobile ? 140 : 220, maxWidth: isMobile ? 260 : 360 }}>
      <IconButton
        onClick={toggle}
        size="small"
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: "rgba(0,0,0,0.08)",
        }}
      >
        {playing ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      <Box onClick={seekClick} style={{ flex: 1, cursor: "pointer" }}>
        <Box style={{ height: 2, background: "rgba(0,0,0,0.25)", borderRadius: 2 }}>
          <Box
            style={{
              width: `${Math.round(progress * 100)}%`,
              height: 2,
              background: "#25D366",
              borderRadius: 2,
            }}
          />
        </Box>
      </Box>

      {/* 🔹 Exibe duração total mesmo parado */}
      <Typography
        variant="caption"
        style={{
          color: "#667085",
          minWidth: 40,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmt(duration)}
      </Typography>

      <audio ref={audioRef} src={src} preload="metadata" style={{ display: "none" }} />
    </Box>
  );
};

export default function ChatWindow({
  mensagens: mensagensProp = [],
  chat,
  onSend,
  placeholder = "Digite uma mensagem...",
  onBack = null,
}) {
  const theme = useTheme();
  // único isMobile (usa breakpoints do tema) — evita re-declaração
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // NÃO retornar cedo — hooks abaixo devem executar sempre para manter ordem previsível
  // o placeholder "Selecione um contato..." será renderizado mais abaixo apenas quando chat === null

  // 🔁 Converte arquivo em base64
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /* 🖼️ Enviar imagem */
  const handleSendImage = async (fileOrObj) => {
    if (!fileOrObj || !chat) return;

    // 1) Descobrir se é vídeo
    let file = fileOrObj.file || fileOrObj;
    let mime = file?.type || "";

    // se veio só base64 (do preview), tentamos detectar pelo prefixo
    if (!mime && fileOrObj.base64 && typeof fileOrObj.base64 === "string") {
      const b64 = fileOrObj.base64;
      const match = b64.match(/^data:(.+?);base64,/);
      if (match && match[1]) {
        mime = match[1]; // ex: "video/mp4" ou "image/jpeg"
      }
    }

    const name = file.name || fileOrObj.name || "midia";
    const isVideo =
      mime.startsWith("video/") ||
      /\.(mp4|mov|webm|mkv)$/i.test(name);

    const tempId = `temp-media-${Date.now()}`;

    setUploadsEmProgresso((prev) => [
      ...prev,
      { id: tempId, tipo: isVideo ? "video" : "image", nome: name },
    ]);

    try {
      let base64;
      let captionToSend = null;

      if (fileOrObj.base64) {
        base64 = fileOrObj.base64;
        captionToSend =
          fileOrObj.caption &&
            typeof fileOrObj.caption === "string" &&
            fileOrObj.caption.trim()
            ? fileOrObj.caption.trim()
            : null;
      } else {
        base64 = await toBase64(file);
        // sem legenda automática
      }

      const rota = isVideo ? "/chat/send/video" : "/chat/send/image";
      console.log(fileOrObj);
      const formData = new FormData();
      formData.append("number", chat.telefone);
      formData.append("file", fileOrObj.file);
      formData.append("nome", chat.nome);
      if (captionToSend) {
        formData.append("caption", captionToSend);
      }
      const response = await api.post(rota, formData);

      const urlCampo = isVideo ? "videoUrl" : "imageUrl";

      toast.success(isVideo ? "📹 Vídeo enviado!" : "🖼️ Imagem enviada!");
    } catch (err) {
      console.error("❌ Erro ao enviar mídia:", err);
      toast.error(isVideo ? "Erro ao enviar vídeo" : "Erro ao enviar imagem");
    } finally {
      setUploadsEmProgresso((prev) => prev.filter((u) => u.id !== tempId));
    }
  };

  /* 📄 Enviar documento */
  const handleSendDocument = async (file) => {
    if (!file || !chat) return;

    const tempId = `temp-doc-${Date.now()}`;
    setUploadsEmProgresso((prev) => [
      ...prev,
      { id: tempId, tipo: "document", nome: file.name },
    ]);

    try {
      const formData = new FormData();
      formData.append("number", chat.telefone);
      formData.append("file", file);
      formData.append("filename", file.name);
      formData.append("caption", file.name);
      formData.append("nome", chat.nome);

      const response = await api.post("/chat/send/document", formData);

      toast.success("📄 Documento enviado!");
    } catch (err) {
      console.error("❌ Erro ao enviar documento:", err);
      toast.error("Erro ao enviar documento");
    } finally {
      setUploadsEmProgresso((prev) => prev.filter((u) => u.id !== tempId));
    }
  };

  /* 🎧 Enviar áudio */
  const handleSendAudio = async (fileOrObj) => {
    if (!fileOrObj || !chat) return;

    // Pode vir um File/Blob (upload) ou um objeto { base64 } (gravação)
    const isFileLike = fileOrObj instanceof Blob || fileOrObj instanceof File;

    const tempId = `temp-audio-${Date.now()}`;

    setUploadsEmProgresso((prev) => [
      ...prev,
      {
        id: tempId,
        tipo: "audio",
        nome: isFileLike ? (fileOrObj.name || "audio") : "Gravação de áudio",
      },
    ]);

    try {
      let audioBase64;

      if (isFileLike) {
        audioBase64 = await toBase64(fileOrObj);
      } else if (fileOrObj.base64 && typeof fileOrObj.base64 === "string") {
        audioBase64 = fileOrObj.base64;
      } else {
        throw new Error("Formato de áudio inválido");
      }

      const response = await api.post("/chat/send/audio", {
        number: chat.telefone,
        audio: audioBase64,
        ptt: true,
        nome: chat.nome,
      });

      const novaMensagem = {
        id: `local-audio-${Date.now()}`,
        chatId: chat.id,
        texto: null,
        tipo: "audio",
        midiaUrl: response.data?.audioUrl || audioBase64,
        direcao: "out",
        remetente: "atendente",
        dataHora: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        local: true,
      };

      setMensagens((prev) => [...prev, novaMensagem]);
      toast.success("🎧 Áudio enviado!");
    } catch (err) {
      console.error("❌ Erro ao enviar áudio:", err);
      toast.error("Erro ao enviar áudio");
    } finally {
      setUploadsEmProgresso((prev) => prev.filter((u) => u.id !== tempId));
    }
  };

  /* 🎙 Controle de gravação */
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);

      toast("🎙 Gravando áudio...");
    } catch (err) {
      console.error("❌ Erro ao acessar microfone:", err);
      toast.error("Erro ao acessar o microfone");
    }
  };

  const stopRecording = async () => {
    try {
      clearInterval(timerRef.current);
      setRecording(false);
      setRecordingTime(0);

      const recorder = mediaRecorderRef.current;
      if (!recorder) return;

      return new Promise((resolve) => {
        recorder.onstop = async () => {
          const blob = new Blob(audioChunksRef.current, { type: audioChunksRef.current[0]?.type || "audio/webm" });
          const base64 = await toBase64(blob);
          resolve(base64);
        };

        recorder.stop();
        recorder.stream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current = null;
      });
    } catch (err) {
      console.error("Erro ao parar gravação:", err);
    }
  };

  const finalizeRecording = async () => {
    try {
      const base64 = await stopRecording();
      if (!base64) return toast.error("Nenhum áudio gravado.");
      await handleSendAudio({ base64 });
    } catch (err) {
      console.error("❌ Erro ao enviar áudio:", err);
      toast.error("Erro ao enviar áudio");
    }
  };

  const [mensagens, setMensagens] = React.useState(mensagensProp);
  // 🔄 Sincroniza mensagens externas (socket/Typebot) sem apagar as locais
  React.useEffect(() => {
    if (!Array.isArray(mensagensProp)) return;

    setMensagens((prev) => {
      const existentes = new Set(
        prev.map((m) => (m.midiaUrl ? m.midiaUrl : m.texto))
      );
      const novas = mensagensProp.filter(
        (m) =>
          !existentes.has(m.midiaUrl ? m.midiaUrl : m.texto) &&
          !m.local // 👈 ignora as que já são locais
      );
      return [...prev, ...novas];
    });
  }, [mensagensProp]);
  const [texto, setTexto] = React.useState("");
  const listRef = React.useRef(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [pdfPreview, setPdfPreview] = React.useState(null);
  const [htmlPreview, setHtmlPreview] = React.useState(null);
  const [textPreview, setTextPreview] = React.useState(null); // { title, content }
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = React.useState(false);
  const panStartRef = React.useRef({ x: 0, y: 0 });
  const offsetStartRef = React.useRef({ x: 0, y: 0 });
  const pinchStartDistRef = React.useRef(null);
  const pinchStartZoomRef = React.useRef(1);
  const [caption, setCaption] = useState("");
  const [imageToSend, setImageToSend] = useState(null);
  const [uploadsEmProgresso, setUploadsEmProgresso] = useState([]);

  const downloadFile = async (url, preferredName) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const nameFromUrl = (url.split("/").pop() || "arquivo").split("?")[0] || "arquivo";
      a.href = blobUrl;
      a.download = preferredName || nameFromUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = preferredName || "";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch { }
    }
  };

  React.useEffect(() => {
    if (imagePreview) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setIsPanning(false);
    }
  }, [imagePreview]);

  // 📜 Scroll automático até o fim quando mensagens ou uploads mudam
  React.useEffect(() => {
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }, 300); // pequeno delay para garantir renderização
  }, [mensagens, uploadsEmProgresso]); // 👈 agora observa uploads também

  React.useEffect(() => {
    if (!chat) return;

    // Força sempre descer ao abrir o chat — primeira renderização costuma falhar
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }, 40); // render inicial

    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }, 150); // render mais completo (imagens / PDFs / vídeos)

    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }, 300); // garante em máquinas lentas

  }, [chat]);


  const baseURL = process.env.REACT_APP_API
    ? process.env.REACT_APP_API
    : `${window.location.origin}`;
  console.log(baseURL);

  const normalizarMidia = (midia) => {
    if (!midia) return null;

    // se já é data:, blob: ou http/https, retorna como está
    if (
      midia.startsWith("data:") ||
      midia.startsWith("blob:") ||
      /^https?:\/\//i.test(midia)
    ) {
      return midia;
    }

    const baseURL = process.env.REACT_APP_API
      ? process.env.REACT_APP_API
      : `${window.location.origin}`;

    // trata caminhos relativos (ex.: /public/..., uploads/..., etc.)
    const clean = midia.replace(/^\/+|^public\//g, "");
    return `${baseURL}/${clean}`;
  };

  // Normaliza "tipo" vindo do backend (aceita PT/EN e mimetypes)
  const normalizarTipo = (tipoRaw, midiaUrl) => {
    const t = (tipoRaw || "").toString().toLowerCase();
    const semAcento = t.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // Já é mimetype (image/jpeg, video/mp4, audio/ogg, etc.)
    if (/^(image|video|audio)\//.test(semAcento)) {
      return semAcento.split("/")[0];
    }

    // Sinonimos PT/EN
    if (semAcento.includes("image") || semAcento.includes("imagem")) return "image";
    if (semAcento.includes("video") || semAcento.includes("video")) return "video";
    if (semAcento.includes("audio") || semAcento.includes("audi")) return "audio";
    if (semAcento.includes("application/pdf") || semAcento === "pdf") return "pdf";
    if (
      semAcento.includes("text/") ||
      semAcento.includes("plain") ||
      semAcento.includes("csv") ||
      semAcento.includes("json") ||
      semAcento.includes("xml")
    ) return "text";
    if (semAcento.includes("html")) return "html";

    // Trata variações de 'documento' e 'documento 📄'
    if (semAcento.includes("documento") || semAcento.includes("document") || semAcento.includes("documento 📄") || semAcento.includes("document 📄")) return "document";

    // Inferir pelo URL/arquivo
    const url = (midiaUrl || "").toString().toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp|bmp)$/.test(url)) return "image";
    if (/\.(mp4|mov|webm|mkv)$/.test(url)) return "video";
    if (/\.(mp3|ogg|wav|m4a|aac)$/.test(url)) return "audio";
    if (/\.(pdf)$/.test(url)) return "pdf";
    if (/\.(txt|csv|json|xml|log)$/.test(url)) return "text";
    if (/\.(html|htm)$/.test(url)) return "html";
    if (/\.(doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z)$/.test(url)) return "document";

    return "text";
  };

  const handleSendEvent = () => {
    if (chat?.status === "finalizado") {
      toast.warning("? Este pedido já foi finalizado. Mensagens bloqueadas.");
      return;
    }

    if (!texto.trim()) return;
    const phone = chat?.numero || chat?.phone || chat?.telefone;
    const body = texto.trim();
    if (typeof onSend === "function") onSend({ Phone: phone, Body: body });
    setTexto("");
  };

  // const handlePedidoFeito = async () => {
  //   if (chat?.status === "finalizado") {
  //     toast.info("?? Este pedido já está finalizado.");
  //     return;
  //   }

  //   try {
  //     const response = await fetch(
  //       `http://localhost:8088/api/chats/${chat.id}/finalizar`,
  //       { method: "PATCH" }
  //     );
  //     const data = await response.json();
  //     if (data.success) {
  //       toast.success("? Pedido criado com sucesso!");
  //     } else {
  //       toast.warn("?? Erro ao criar pedido");
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("? Erro de comunicação com o servidor");
  //   }
  // };

  const renderMensagem = (msg) => {
    const midia = msg.midiaUrl || msg.midia || msg.url || null;
    const src = normalizarMidia(midia);
    const tipoNorm = normalizarTipo(msg.tipo, src);
    // Se não há mídia associada, renderiza como texto puro
    if (!midia && !src) {
      return (
        <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
          {msg.texto || msg.body || ""}
        </Typography>
      );
    }
    const fileName = msg.fileName || msg.filename || msg.nomeArquivo || msg.texto || msg.body || null;

    if (src && tipoNorm === "image")
      return (
        <>
          <img
            src={src}
            alt="imagem"
            style={{
              maxWidth: isMobile ? 220 : 260,
              borderRadius: 10,
              marginBottom: 4,
              display: "block",
              cursor: "pointer",
            }}
            onClick={() => setImagePreview(src)}
            onError={(e) => (e.target.style.display = "none")}
          />
          {msg.texto && (
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
              {msg.texto}
            </Typography>
          )}
        </>
      );

    if (src && tipoNorm === "video")
      return (
        <>
          <video
            src={src}
            controls
            style={{
              maxWidth: isMobile ? 220 : 260,
              borderRadius: 10,
              marginBottom: 4,
              display: "block",
              cursor: "pointer",
            }}
            onClick={() => setImagePreview(src)} // usa o mesmo Dialog para vídeo
            onError={(e) => (e.target.style.display = "none")}
          />
          {msg.texto && (
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
              {msg.texto}
            </Typography>
          )}
        </>
      );

    if (src && tipoNorm === "audio")
      return (
        <AudioBubble src={src} />
      );

    // PDF preview
    if (tipoNorm === "pdf")
      return (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
            {fileName ? ("📄 PDF · " + fileName) : "📄 PDF"}
          </Typography>
          {src ? (
            <Box display="flex" gridGap={8}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => setPdfPreview(src)}
              >
                Visualizar
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => downloadFile(src, fileName || undefined)}
              >
                Baixar
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Arquivo indisponível para visualização. Aguarde o download do servidor.
            </Typography>
          )}
        </Box>
      );

    // HTML preview
    if (tipoNorm === "html")
      return (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
            {fileName ? ("🌐 HTML · " + fileName) : "🌐 HTML"}
          </Typography>
          {src ? (
            <Box display="flex" gridGap={8}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => setHtmlPreview(src)}
              >
                Visualizar
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => downloadFile(src, fileName || undefined)}
              >
                Baixar
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Arquivo indisponível para visualização.
            </Typography>
          )}
        </Box>
      );


    // Text-like preview (txt, csv, json, xml)
    if (tipoNorm === "text")
      return (
        <Box>
          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 4 }}>
            <DescriptionIcon style={{ color: "#6b7280" }} />
            <Typography variant="body2" style={{ fontWeight: 600 }}>{"Texto" + (fileName ? " · " + fileName : "")}</Typography>
          </Box>
          {src ? (
            <Box display="flex" gridGap={8}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={async () => {
                  try {
                    const res = await fetch(src);
                    const text = await res.text();
                    setTextPreview({ title: fileName || "Arquivo de texto", content: text });
                  } catch {
                    setTextPreview({ title: fileName || "Arquivo de texto", content: "Erro ao carregar conteúdo." });
                  }
                }}
              >
                Visualizar
              </Button>
              <Button size="small" variant="outlined" onClick={() => downloadFile(src, fileName || undefined)}>
                Baixar
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Arquivo indisponível para visualização.
            </Typography>
          )}
        </Box>
      );

    // Generic document fallback (doc, xls, ppt, zip, etc.)
    if (tipoNorm === "document" || tipoNorm === "unknown")
      return (
        <Box>
          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 4 }}>
            <DescriptionIcon style={{ color: "#6b7280" }} />
            <Typography variant="body2" style={{ fontWeight: 600 }}>{"Arquivo" + (fileName ? " · " + fileName : "")}</Typography>
          </Box>
          {src ? (
            <Box display="flex" gridGap={8}>
              <Button size="small" variant="outlined" onClick={() => downloadFile(src, fileName || undefined)}>
                Baixar
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Arquivo indisponível para visualização. Aguarde o download do servidor.
            </Typography>
          )}
        </Box>
      );

    return (
      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
        {msg.texto || msg.body || ""}
      </Typography>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      height="100%"
      style={{
        backgroundImage: "url('/bkg_wpp.png')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        backgroundPosition: "top left",
        backgroundAttachment: isMobile ? "scroll" : "fixed",
      }}
    >
      {/* 🛒 Carrinho manual aparece apenas quando o chat está "aberto" */}
      {chat?.status === "aberto" && (
        <Box
          position="absolute"
          bottom={isMobile ? 220 : 140} // sobe mais para ficar acima do composer
          right={isMobile ? 8 : 20}
          left={isMobile ? 8 : "auto"}
          zIndex={10}
          bgcolor="transparent"
          borderRadius={12}
          p={1.5}
          sx={{
            maxWidth: isMobile ? "calc(100% - 40px)" : 300,
            transition: "all 0.3s ease",
          }}
        >
          <Carrinho
            chat={chat}
            onMensagemEnviada={(mensagem) => {
              // adiciona nova mensagem ao estado atual
              if (typeof mensagem === "string") {
                setMensagens((prev) => [
                  ...prev,
                  {
                    texto: mensagem,
                    direcao: "out",
                    tipo: "text",
                    remetente: "atendente",
                    local: true, // 👈
                    dataHora: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  },
                ]);
              } else {
                setMensagens((prev) => [...prev, mensagem]);
              }
            }}
          />

        </Box>
      )}

      {/* ?? Lista de mensagens */}
      <Box
        ref={listRef}
        flexGrow={1}
        overflow="auto"
        p={2}
        style={{
          backgroundColor: "transparent",
          boxSizing: "border-box",
        }}
      >
        {(!mensagens || mensagens.length === 0) && uploadsEmProgresso.length === 0 ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {!chat && (
              <Typography color="textSecondary">
                Selecione um contato para começar a conversar.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {mensagens
              .map((msg) => ({
                ...msg,
                texto: msg.texto || msg.body || "",
              }))
              .filter((msg, index, self) => {
                const hasTexto = (msg.texto || "").trim() !== "";
                const hasMidia = !!(msg.midiaUrl || msg.midia || msg.url);
                if (!hasTexto && !hasMidia) return false;

                // Sempre renderiza textos puros (sem mídia)
                if (hasTexto && !hasMidia) return true;

                // Permite textos e mídias consecutivas diferentes
                return (
                  index ===
                  self.findIndex((m) => {
                    const sameText = (m.texto || "") === (msg.texto || "");
                    const sameMidia =
                      (m.midiaUrl && msg.midiaUrl && m.midiaUrl === msg.midiaUrl) ||
                      (m.url && msg.url && m.url === msg.url);
                    const timeDiff = Math.abs(
                      new Date(m.createdAt || m.dataHora) -
                      new Date(msg.createdAt || msg.dataHora)
                    );
                    // Filtra apenas se ambos texto e midia forem iguais e próximas
                    return (sameText && sameMidia && timeDiff < 5000);
                  })
                );
              })
              .map((msg, i) => {
                let direcao = msg?.direcao;
                const remetente = (msg?.remetente || "").toLowerCase();
                const midia = msg.midiaUrl || msg.midia || msg.url || "";

                if (msg.local === true) direcao = "out";

                if (!direcao && typeof midia === "string" && midia.startsWith("data:")) {
                  direcao = "out";
                }

                if (
                  (!direcao || direcao === "in") &&
                  msg.tipo === "audio" &&
                  (midia.startsWith("data:") || msg.ptt === true)
                ) {
                  direcao = "out";
                }

                if (!direcao) {
                  if (remetente === "operador") direcao = "out";
                  else if (remetente === "cliente") direcao = "in";
                }

                if (!direcao) direcao = "in";

                const isOperador = direcao === "out";

                // console.log("💬 Mensagem normalizada:", {
                //   texto: msg.texto,
                //   tipo: msg.tipo,
                //   direcao,
                //   remetente,
                //   tipoEnvio: msg.tipoEnvio,
                //   from: msg.from,
                //   midiaUrl: msg.midiaUrl?.slice(0, 60),
                // });

                const hora = (() => {
                  const dt = msg.dataHora || msg.createdAt || msg.timestamp || msg.date || null;
                  if (!dt) return null;
                  try {
                    return new Date(dt).toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                  } catch {
                    return "";
                  }
                })();

                return (
                  <Box
                    key={msg.id || i}
                    mb={1.2}
                    display="flex"
                    justifyContent={isOperador ? "flex-end" : "flex-start"}
                  >
                    <Box
                      style={{
                        background: isOperador ? "#d9fdd3" : "#fff",
                        color: "#111",
                        padding: "8px 12px 16px 12px",
                        paddingRight: 48,
                        borderRadius: 12,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                        maxWidth: isMobile ? "85%" : "75%",
                        wordBreak: "break-word",
                        borderTopLeftRadius: isOperador ? 12 : 0,
                        borderTopRightRadius: isOperador ? 0 : 12,
                        position: "relative",
                      }}
                    >
                      {renderMensagem(msg)}

                      {hora && (
                        <Typography
                          variant="caption"
                          style={{
                            position: "absolute",
                            right: 8,
                            bottom: 6,
                            fontSize: 10,
                            lineHeight: 1,
                            color: "#94a3b8",
                            opacity: 0.9,
                            background: "transparent",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {hora}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}

            {/* Uploads em progresso - renderiza DEPOIS das mensagens */}
            {uploadsEmProgresso.map((upload) => (
              <Box
                key={upload.id}
                mb={1.2}
                display="flex"
                justifyContent="flex-end"
              >
                <Box
                  bgcolor="#d9fdd3"
                  p={1.5}
                  borderRadius={2}
                  maxWidth={isMobile ? "75%" : "60%"}
                  boxShadow="0 1px 2px rgba(0,0,0,0.15)"
                  style={{ borderTopRightRadius: 0 }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={16} style={{ color: "#128c7e" }} />
                    <Typography variant="body2" style={{ fontSize: 13, color: "#374151" }}>
                      {upload.tipo === "image"
                        ? "Enviando imagem..."
                        : upload.tipo === "video"
                          ? "Enviando vídeo..."
                          : upload.tipo === "audio"
                            ? "Enviando áudio..."
                            : "Enviando documento..."}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* 💬 Campo de envio — só mostra quando houver um chat aberto */}
      {chat ? (
        <Box
          display="flex"
          flexDirection="column"
          bgcolor="#f0f2f5"     // restaura a barra/composer ao visual original
          borderTop="1px solid #e0e0e0"
          style={{
            flexShrink: 0,
            position: "sticky",
            bottom: 0,
            zIndex: 2,
          }}
        >
          {/* ─ todo o conteúdo do composer (botões, campo de texto, previews, etc.) fica aqui */}
          {/* ✅ Aviso se finalizado */}
          {chat?.status === "finalizado" && (
            <Box
              bgcolor="#fef3c7"
              borderTop="1px solid #fcd34d"
              p={1.2}
              textAlign="center"
            >
              <Typography
                variant="body2"
                style={{
                  color: "#92400e",
                  fontWeight: 600,
                }}
              >
                ✅ Atendimento Finalizado!
              </Typography>
            </Box>
          )}

          {/* Linha única igual WhatsApp */}
          <Box
            display="flex"
            alignItems="center"
            p={1}
            style={{
              gap: 8,
              backgroundColor: "transparent",
            }}
          >
            {/* 📎 Documento */}
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={chat?.status === "finalizado"}
              style={{ minWidth: 42 }}
            >
              📎
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleSendDocument(file);
                  e.target.value = null;
                }}
              />
            </Button>

            {/* 🖼️ Botão de imagem */}
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={chat?.status === "finalizado"}
              style={{ minWidth: 42 }}
            >
              🖼️
              <input
                type="file"
                accept="image/*,video/*"
                hidden
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;

                  try {
                    const base64 = await toBase64(file);
                    const mime = file.type || "";
                    const isVideo =
                      mime.startsWith("video/") ||
                      /\.(mp4|mov|webm|mkv)$/i.test(file.name || "");

                    setImageToSend({ file, base64, isVideo });
                    setCaption("");
                  } catch (err) {
                    console.error(err);
                  } finally {
                    e.target.value = null;
                  }
                }}
              />
            </Button>

            {/* PREVIEW IGUAL WHATSAPP */}
            {imageToSend && (
              <Box
                position="absolute"
                bottom={isMobile ? 120 : 70}
                left={isMobile ? 8 : 20}
                zIndex={50}
                p={1.5}
                bgcolor="#fff"
                borderRadius={8}
                boxShadow="0 4px 14px rgba(0,0,0,0.25)"
                style={{
                  width: isMobile ? "calc(100% - 32px)" : 290,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  border: "1px solid #e4e4e4",
                }}
              >
                {imageToSend.isVideo ? (
                  <video
                    src={imageToSend.base64}
                    controls
                    style={{
                      width: "100%",
                      maxHeight: 240,
                      objectFit: "contain",
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <img
                    src={imageToSend.base64}
                    alt="preview"
                    style={{
                      width: "100%",
                      maxHeight: 240,
                      objectFit: "contain",
                      borderRadius: 8,
                    }}
                  />
                )}

                <TextField
                  placeholder="Adicionar legenda..."
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  style={{ gap: 12 }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setImageToSend(null);
                      setCaption("");
                    }}
                    style={{
                      background: "#f0f2f5",
                      color: "#3b4a54",
                      borderRadius: 8,
                      padding: "4px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                      textTransform: "none",
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    style={{
                      padding: "4px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 8,
                      textTransform: "none",
                    }}
                    onClick={() => {
                      handleSendImage({
                        base64: imageToSend.base64,
                        // name: imageToSend.name,
                        file: imageToSend.file,
                        caption,
                      });

                      setImageToSend(null);
                      setCaption("");
                    }}
                  >
                    Enviar
                  </Button>
                </Box>
              </Box>
            )}



            {/* ✏️ Campo de texto */}
            <TextField
              fullWidth
              variant="outlined"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={
                recording
                  ? "Gravando áudio..."
                  : chat?.status === "finalizado"
                    ? "Chat finalizado"
                    : placeholder
              }
              onKeyDown={(e) => e.key === "Enter" && !recording && handleSendEvent()}
              size="small"
              disabled={chat?.status === "finalizado"}
              style={{
                background: "#fff", // input branco (visualmente separado da barra)
                borderRadius: 8,
              }}
            />

            {/* 📤 Enviar */}
            <IconButton
              onClick={recording ? finalizeRecording : handleSendEvent}
              color="primary"
              disabled={chat?.status === "finalizado"}
              style={{
                background: "#25D366",
                color: "#fff",
                width: 42,
                height: 42,
              }}
            >
              ➤
            </IconButton>

            {/* 🎙️ Gravação */}
            {recording ? (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography
                  variant="body2"
                  style={{ color: "#dc2626", minWidth: 55, fontWeight: 600 }}
                >
                  ⏺ {recordingTime}s
                </Typography>
                <IconButton
                  onClick={stopRecording}
                  style={{
                    background: "#dc2626",
                    color: "#fff",
                    width: 42,
                    height: 42,
                  }}
                  title="Cancelar gravação"
                >
                  <FaStop size={16} />
                </IconButton>
              </Box>
            ) : (
              <IconButton
                onClick={startRecording}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  width: 42,
                  height: 42,
                }}
                title="Gravar áudio"
              >
                <FaMicrophone size={16} />
              </IconButton>
            )}


            <Box ml={1}>
              {/* <Tooltip title="Mover conversa para pedidos">
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<DoneAllIcon />}
                  style={{
                    backgroundColor: "#25D366",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                  onClick={handlePedidoFeito}
                  disabled={chat?.status === "finalizado"}
                >
                  Pedido Feito
                </Button>
              </Tooltip> */}
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* Visualização de imagem (lightbox aprimorado) */}
      <Dialog
        open={Boolean(imagePreview)}
        fullScreen={isMobile}
        maxWidth="xl"
        fullWidth
        keepMounted
        PaperProps={{
          style: { background: "transparent", boxShadow: "none", overflow: "visible" },
        }}
        BackdropProps={{
          style: { backgroundColor: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)" },
        }}
      >
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={0}
          style={{ height: "90vh", width: "100%" }}
          onClick={() => setImagePreview(null)}
        >
          {/* Botão fechar */}
          <IconButton
            onClick={() => setImagePreview(null)}
            style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.4)", color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>

          {/* Área de imagem com zoom/pan */}
          <Box
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={() => {
              if (zoom > 1) {
                setZoom(1);
                setOffset({ x: 0, y: 0 });
              } else {
                setZoom(2);
              }
            }}
            onWheel={(e) => {
              e.preventDefault();
              const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              setZoom((z) => clamp(Number((z + delta).toFixed(2)), 1, 4));
            }}
            onMouseDown={(e) => {
              if (zoom <= 1) return;
              setIsPanning(true);
              panStartRef.current = { x: e.clientX, y: e.clientY };
              offsetStartRef.current = { ...offset };
            }}
            onMouseMove={(e) => {
              if (!isPanning) return;
              const dx = e.clientX - panStartRef.current.x;
              const dy = e.clientY - panStartRef.current.y;
              setOffset({ x: offsetStartRef.current.x + dx, y: offsetStartRef.current.y + dy });
            }}
            onMouseUp={() => setIsPanning(false)}
            onMouseLeave={() => setIsPanning(false)}
            onTouchStart={(e) => {
              if (e.touches.length === 2) {
                const d = Math.hypot(
                  e.touches[0].clientX - e.touches[1].clientX,
                  e.touches[0].clientY - e.touches[1].clientY
                );
                pinchStartDistRef.current = d;
                pinchStartZoomRef.current = zoom;
              } else if (e.touches.length === 1 && zoom > 1) {
                setIsPanning(true);
                panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                offsetStartRef.current = { ...offset };
              }
            }}
            onTouchMove={(e) => {
              if (e.touches.length === 2 && pinchStartDistRef.current) {
                e.preventDefault();
                const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
                const d = Math.hypot(
                  e.touches[0].clientX - e.touches[1].clientX,
                  e.touches[0].clientY - e.touches[1].clientY
                );
                const scale = d / (pinchStartDistRef.current || d);
                setZoom(clamp(Number((pinchStartZoomRef.current * scale).toFixed(2)), 1, 4));
              } else if (e.touches.length === 1 && isPanning) {
                const dx = e.touches[0].clientX - panStartRef.current.x;
                const dy = e.touches[0].clientY - panStartRef.current.y;
                setOffset({ x: offsetStartRef.current.x + dx, y: offsetStartRef.current.y + dy });
              }
            }}
            onTouchEnd={(e) => {
              if (e.touches.length === 0) {
                setIsPanning(false);
                pinchStartDistRef.current = null;
              }
            }}
            style={{
              maxWidth: "90vw",
              maxHeight: "80vh",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
              cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "zoom-in",
              background: "rgba(255,255,255,0.02)",
            }}
          >
            {imagePreview && (
              <>
                {/\.(mp4|mov|webm|mkv)$/i.test(imagePreview) ? (
                  <video
                    src={imagePreview}
                    controls
                    style={{
                      display: "block",
                      maxWidth: "90vw",
                      maxHeight: "80vh",
                      transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                      transformOrigin: "center center",
                      transition: isPanning ? "none" : "transform 150ms ease-out",
                      userSelect: "none",
                    }}
                  />
                ) : (
                  <img
                    src={imagePreview}
                    alt="visualização"
                    draggable={false}
                    style={{
                      display: "block",
                      maxWidth: "90vw",
                      maxHeight: "80vh",
                      transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                      transformOrigin: "center center",
                      transition: isPanning ? "none" : "transform 150ms ease-out",
                      userSelect: "none",
                    }}
                  />
                )}
              </>
            )}

          </Box>

          {/* Toolbar */}
          <Box
            onClick={(e) => e.stopPropagation()}
            position="absolute"
            bottom={16}
            display="flex"
            alignItems="center"
            style={{
              background: "rgba(17,24,39,0.6)",
              color: "#fff",
              borderRadius: 999,
              boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
              padding: "6px 10px",
              backdropFilter: "blur(4px)",
            }}
          >
            <IconButton onClick={() => setZoom((z) => Math.max(1, Number((z - 0.2).toFixed(2))))} style={{ color: "#fff" }}>
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={() => setZoom((z) => Math.min(4, Number((z + 0.2).toFixed(2))))} style={{ color: "#fff" }}>
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }} style={{ color: "#fff" }}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={() => downloadFile(imagePreview)} style={{ color: "#fff" }}>
              <GetAppIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>

      {/* Visualização de PDF */}
      <Dialog
        open={Boolean(pdfPreview)}
        fullScreen={isMobile}
        maxWidth="xl"
        fullWidth
        keepMounted
        PaperProps={{
          style: { background: "transparent", boxShadow: "none", overflow: "visible" },
        }}
        BackdropProps={{
          style: { backgroundColor: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)" },
        }}
      >
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={0}
          style={{ height: "90vh", width: "100%" }}
          onClick={() => setPdfPreview(null)}
        >
          <IconButton
            onClick={() => setPdfPreview(null)}
            style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.4)", color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>

          <Box onClick={(e) => e.stopPropagation()} style={{ width: "90vw", height: "80vh", borderRadius: 12, overflow: "hidden", boxShadow: "0 12px 30px rgba(0,0,0,0.25)" }}>
            {pdfPreview && (
              <iframe
                title="PDF"
                src={pdfPreview}
                style={{ width: "100%", height: "100%", border: "none", background: "#fff" }}
              />)
            }
          </Box>

          <Box
            onClick={(e) => e.stopPropagation()}
            position="absolute"
            bottom={16}
            display="flex"
            alignItems="center"
            style={{
              background: "rgba(17,24,39,0.6)",
              color: "#fff",
              borderRadius: 999,
              boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
              padding: "6px 10px",
              backdropFilter: "blur(4px)",
            }}
          >
            <IconButton onClick={() => downloadFile(pdfPreview)} style={{ color: "#fff" }}>
              <GetAppIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>

      {/* Visualização de HTML */}
      <Dialog
        open={Boolean(htmlPreview)}
        fullScreen={isMobile}
        maxWidth="xl"
        fullWidth
        keepMounted
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "visible" } }}
        BackdropProps={{ style: { backgroundColor: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)" } }}
      >
        <Box position="relative" display="flex" alignItems="center" justifyContent="center" p={0} style={{ height: "90vh", width: "100%" }} onClick={() => setHtmlPreview(null)}>
          <IconButton onClick={() => setHtmlPreview(null)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.4)", color: "#fff" }}>
            <CloseIcon />
          </IconButton>
          <Box onClick={(e) => e.stopPropagation()} style={{ width: "90vw", height: "80vh", borderRadius: 12, overflow: "hidden", boxShadow: "0 12px 30px rgba(0,0,0,0.25)", background: "#fff" }}>
            {htmlPreview && <iframe title="HTML" src={htmlPreview} style={{ width: "100%", height: "100%", border: "none" }} />}
          </Box>
          <Box onClick={(e) => e.stopPropagation()} position="absolute" bottom={16} display="flex" alignItems="center" style={{ background: "rgba(17,24,39,0.6)", color: "#fff", borderRadius: 999, boxShadow: "0 6px 20px rgba(0,0,0,0.25)", padding: "6px 10px", backdropFilter: "blur(4px)" }}>
            <IconButton onClick={() => downloadFile(htmlPreview)} style={{ color: "#fff" }}>
              <GetAppIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>

      {/* Visualização de Texto */}
      <Dialog
        open={Boolean(textPreview)}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
        keepMounted
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "visible" } }}
        BackdropProps={{ style: { backgroundColor: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)" } }}
      >
        <Box position="relative" display="flex" alignItems="center" justifyContent="center" p={0} style={{ height: "85vh", width: "100%" }} onClick={() => setTextPreview(null)}>
          <IconButton onClick={() => setTextPreview(null)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.4)", color: "#fff" }}>
            <CloseIcon />
          </IconButton>
          <Box onClick={(e) => e.stopPropagation()} style={{ width: "85vw", height: "75vh", borderRadius: 12, overflow: "hidden", boxShadow: "0 12px 30px rgba(0,0,0,0.25)", background: "#0b1020" }}>
            <Box p={2} style={{ height: "100%", overflow: "auto" }}>
              <Typography variant="subtitle2" style={{ color: "#cbd5e1", marginBottom: 8 }}>
                {textPreview?.title || "Texto"}
              </Typography>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#e5e7eb", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" }}>
                {textPreview?.content || ""}
              </pre>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

// socket.on("novaMensagem", (payload) => {
//   console.log("🔔 Socket novaMensagem recebida:", payload); // <-- adicione esta linha
//   const msg = payload?.mensagem ?? payload;
//   // ... resto do código
// });
